import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Modal, Select, Space, Table, Tag, message } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import docsService from '../services/docsService'

import { adminPath } from '../../shared/config/paths';

const locales = [
  { code: 'en', label: 'English' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'ms', label: 'Bahasa Melayu' }
]

const DocsManager = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [pages, setPages] = useState([])
  const [activeLocale, setActiveLocale] = useState('en')

  const filteredPages = useMemo(
    () => pages.filter((p) => p.locale === activeLocale || !activeLocale),
    [pages, activeLocale]
  )

  const loadPages = async () => {
    setLoading(true)
    try {
      const res = await docsService.list({ locale: activeLocale })
      const items = res.data?.items || res.items || res.data || []
      setPages(Array.isArray(items) ? items : [])
    } catch (err) {
      message.error(err?.message || '加载文档列表失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPages()
  }, [activeLocale])

  const handleCreate = () => {
    navigate(adminPath('/docs/new'))
  }

  const handleEdit = (record) => {
    navigate(adminPath(`/docs/${record.id}/edit`))
  }

  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除该文档？',
      content: '子节点会被提升到当前节点的父级。',
      okText: '删除',
      okType: 'danger',
      onOk: async () => {
        try {
          await docsService.remove(record.id)
          message.success('已删除')
          loadPages()
        } catch (err) {
          message.error(err?.message || '删除失败')
        }
      }
    })
  }

  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Locale', dataIndex: 'locale', key: 'locale', width: 120 },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (value) => <Tag>{value}</Tag>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value) => (
        <Tag color={value === 'published' ? 'green' : 'default'}>{value}</Tag>
      )
    },
    { title: '排序', dataIndex: 'display_order', key: 'display_order', width: 80 },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ]

  return (
    <Card
      title="Docs 管理"
      extra={
        <Space>
          <Select
            value={activeLocale}
            onChange={setActiveLocale}
            style={{ width: 140 }}
            options={locales.map((l) => ({ label: l.label, value: l.code }))}
          />
          <Button icon={<ReloadOutlined />} onClick={loadPages}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            新建
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredPages}
        columns={columns}
        pagination={{ pageSize: 20 }}
      />
    </Card>
  )
}

export default DocsManager
